import React from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import PropTypes from 'prop-types';
import MenuButton from '@/components/common/MenuButton';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import useConstituent from '@/lib/hooks/swr/useConstituent';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import { useCurrentUser } from '@/lib/hooks/userData';
import useRecentlyViewedConstituents from '@/lib/hooks/useRecentlyViewedConstituents';
import translate from '@/lib/intl/translate';
import { Dialogs, useDialogManager } from '@/modules/Dialogs';
import { deleteConstituent } from '@/services/constituentServices';
import { isPersonConstituentType } from '@/utils/constituentUtils';
import showErrorMessage from '@/utils/toasts/showErrorMessage';
import showSuccessMessage from '@/utils/toasts/showSuccessMessage';
import EditConstituentTypeModal from './EditConstituentTypeModal';

function ConstituentMenu({ constituentId }) {
  const { openDialog, closeDialog } = useDialogManager();
  const { data } = useConstituent(constituentId);
  const history = useHistory();
  const ownerOrgId = useOwnerOrgId();
  const { isOrgAdmin, isOrgEditor, isSuperAdmin } = useCurrentUser(true);
  const id = `constituent-${constituentId}-menu`;
  const [editTypeModalActive, setEditTypeModalActive] = React.useState(false);
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const openMenu = (e) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);
  const { deleteConstituentFromView } = useRecentlyViewedConstituents();

  const canEdit = isOrgAdmin || isOrgEditor;
  const canDelete = isOrgAdmin || isSuperAdmin;

  const items = [
    canEdit && {
      label: translate('EDIT_PROFILE'),
      onClick: () => {
        history.push(
          generatePath(AuthRoutes.CONSTITUENT_EDIT, { constituentId }),
        );
        closeMenu();
      },
    },
    canEdit &&
      data && {
        label: translate(
          isPersonConstituentType(data)
            ? 'CONSTITUENT_CONVERT_TO_ORGANIZATION'
            : 'CONSTITUENT_CONVERT_TO_INDIVIDUAL',
        ),
        onClick: () => {
          setEditTypeModalActive(true);
          closeMenu();
        },
      },
    canDelete && {
      label: (
        <span className="text-error-main">
          {translate('DELETE_CONSTITUENT')}
        </span>
      ),
      onClick: () => {
        closeMenu();
        openDialog(Dialogs.CONFIRMATION, {
          children: translate('DELETE_CONSTITUENT_CONFIRMATION_MESSAGE'),
          confirmLabel: translate('DELETE'),
          headerText: translate('DELETE_CONSTITUENT'),
          onCancel: closeDialog,
          onClose: closeDialog,
          onConfirm: async () => {
            try {
              await deleteConstituent({ constituentId, ownerOrgId });
              deleteConstituentFromView(constituentId);
              history.push(AuthRoutes.CONSTITUENTS);
              showSuccessMessage({
                message: translate('DELETE_CONSTITUENT_SUCCESS_MESSAGE'),
              });
            } catch {
              showErrorMessage({
                message: translate('DELETE_CONSTITUENT_ERROR_MESSAGE', {
                  name: data.displayName,
                }),
              });
            } finally {
              closeDialog();
            }
          },
        });
      },
    },
  ].filter(Boolean);

  return data && items.length ? (
    <>
      <MenuButton
        onClick={openMenu}
        className="ml-4 text-text-secondary"
        aria-controls={menuAnchor ? id : undefined}
        aria-haspopup="true"
      />
      <Menu
        id={id}
        onClose={closeMenu}
        open={!!menuAnchor}
        anchorEl={menuAnchor}
      >
        {items.map(({ label, onClick }) => (
          <MenuItem key={label} onClick={onClick}>
            {label}
          </MenuItem>
        ))}
      </Menu>
      <EditConstituentTypeModal
        open={editTypeModalActive}
        constituentId={constituentId}
        onClose={() => {
          setEditTypeModalActive(false);
        }}
      />
    </>
  ) : null;
}

ConstituentMenu.propTypes = {
  constituentId: PropTypes.string,
};

ConstituentMenu.defaultProps = {
  constituentId: null,
};

export default ConstituentMenu;
